import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationExtras, NavigationStart, Router, UrlTree } from '@angular/router';

import { filter } from 'rxjs';
import { WINDOW } from '@hglobal/common';
import { environment } from '@pt/environment';
import { MfeUrlSegments } from '@pt/mfe';
import { eventEffect } from '@pt/helpers';

@Injectable({ providedIn: 'root' })
export class ExtendedRouter {
  readonly #router = inject(Router);
  readonly #window = inject(WINDOW);
  readonly #mfeUrlSegments = inject(MfeUrlSegments);

  readonly #signinSegment = this.#mfeUrlSegments.signin;
  readonly #previousUrls = ['/'];

  #currentUrl = '/';
  #backUrl = '/';

  readonly navigationStartEvent = toSignal(this.#router.events
    .pipe(filter(event => event instanceof NavigationStart)));

  readonly navigationEndEvent = toSignal(this.#router.events
    .pipe(filter(event => event instanceof NavigationEnd)));

  readonly #navigationStartEffect = eventEffect(
    () => this.navigationStartEvent(),
    event => {
      const newUrl = event.url.split('?')[0];
      const currentUrl = this.#currentUrl.split('?')[0];

      if (newUrl !== currentUrl && !this.isSignInUrl(newUrl)) {
        if (this.#backUrl !== this.#currentUrl) {
          this.#previousUrls.push(this.#currentUrl);
        }
        this.#currentUrl = event.url;
        this.#backUrl = '';
      }
    },
  );

  navigate(commands: any[], extras?: NavigationExtras): Promise<boolean> {
    return this.#router.navigate(commands, extras);
  }

  navigateByUrl(url: string | UrlTree): Promise<boolean> {
    return this.#router.navigateByUrl(url);
  }

  navigateToMain(): Promise<boolean> {
    return this.navigateByUrl('/');
  }

  navigateTo404(): Promise<boolean> {
    return this.navigateByUrl('/404');
  }

  navigateToSignIn(): Promise<boolean> {
    const { pathname } = this.#window.location;
    const path = pathname.includes(this.#signinSegment) ? '' : pathname;
    return this.navigate([`/${this.#signinSegment}`], {
      queryParams: { url: !path || path === '/' ? null : encodeURIComponent(path) },
    });
  }

  navigateBack(): void {
    this.#backUrl = this.#currentUrl;
    this.navigateByUrl(this.#extractPreviousUrl());
  }

  navigateToExternalUrl(url: string): void {
    try {
      const { hostname: targetHost } = new URL(url);
      const { hostname: lmsHost } = new URL(environment.lmsHost);
      const { hostname: lmsTraineeHost } = new URL(environment.lmsTraineeHost);
      const { hostname: currentHost } = this.#window.location;

      if (currentHost === 'localhost'
        || targetHost === currentHost || targetHost.includes(currentHost)
        || targetHost === lmsHost || targetHost.includes(lmsHost)
        || targetHost === lmsTraineeHost || targetHost.includes(lmsTraineeHost)) {
        this.#window.location.href = url;
      } else {
        this.navigateToMain();
      }
    } catch {
      this.navigateToMain();
    }
  }

  isExternalUrl(url: string): boolean {
    return (url.startsWith('http://') || url.startsWith('https://'));
  }

  isSignInUrl(url: string): boolean {
    return url.includes(this.#signinSegment);
  }

  #extractPreviousUrl(): string {
    return this.#previousUrls.pop() || '/';
  }
}
